import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import { SelectObject } from '@components/SelectObject'
import type { Gebied } from '@models/Gebied'
import type { Organisatie } from '@models/Organisatie'
import type { Projectbetrokkene } from '@models/Projectbetrokkene'

import type { Discipline } from '../../../core/util/discipline'
import { useOrganisaties } from '../../../organisaties/hooks/useOrganisaties'
import { StyledError } from '../../../theme/components/StyledError'
import { useCreateProjectbetrokkene } from '../../hooks/useCreateProjectbetrokkene'
import { organisatieOptionsVoorTypes } from '../../util/projectbetrokkene-util'

import { SelectDisciplines } from './SelectDisciplines'

export { CreateVerantwoordelijke }

type Props = {
  onClose: () => void
  gebieden: Gebied[]
  projectType: Projectbetrokkene['projectType']
}

const verantwoordelijkeTypes: Record<
  Projectbetrokkene['projectType'],
  Projectbetrokkene['type'][]
> = {
  Aanvraag: ['Combi'],
  Hoofdleiding: ['Combi', 'Netbeheerder'],
}

const CreateVerantwoordelijke = ({ onClose, gebieden, projectType }: Props) => {
  const { mutate: createProjectbetrokkene, error } =
    useCreateProjectbetrokkene()

  const { data: organisaties } = useOrganisaties()

  const [verantwoordelijke, setVerantwoordelijke] =
    useState<Organisatie | null>(null)
  const [gebied, setGebied] = useState<Gebied | null>(null)
  const [disciplines, setDisciplines] = useState<Discipline[]>([])
  const [errorText, setErrorText] = useState('')
  const [disciplinesErrorText, setDisciplinesErrorText] = useState('')

  const verantwoordelijkeOptions = organisatieOptionsVoorTypes(
    organisaties,
    verantwoordelijkeTypes[projectType],
  )

  const type = verantwoordelijke?.type

  const handleSubmit = () => {
    if (!verantwoordelijke) {
      setErrorText('Verantwoordelijke is verplicht')

      return
    }

    if (!gebied) {
      setErrorText('Gebied is verplicht')

      return
    }

    if (type === 'Netbeheerder' && disciplines.length === 0) {
      setDisciplinesErrorText('Minimaal één discipline is verplicht')

      return
    }

    createProjectbetrokkene(
      {
        projectbetrokkene: {
          projectType,
          verantwoordelijke: verantwoordelijke.organisatie,
          gebiedscode: gebied.code,
          gebiedscodeLabel: '',
          organisatie: verantwoordelijke.organisatie,
          type: verantwoordelijke.type,
          ...(type === 'Netbeheerder' ? { disciplines } : {}),
        },
      },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Verantwoordelijke toevoegen</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <SelectObject
          options={gebieden}
          label="Gebied"
          value={gebied}
          onChange={(value) => {
            if (value) {
              setGebied(value)
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <SelectObject
            label="Verantwoordelijke"
            options={verantwoordelijkeOptions}
            value={verantwoordelijke}
            onChange={(value) => {
              setVerantwoordelijke(value)
              setErrorText('')
            }}
            getOptionLabel={(option) => option.organisatie}
            groupBy={(option) => option.type}
            isOptionEqualToValue={(option, value) =>
              option.organisatie === value.organisatie
            }
            error={!!errorText}
            helperText={errorText}
            sx={{ flex: 1 }}
          />

          <TextField
            label="Type"
            value={verantwoordelijke?.type ?? ''}
            variant="standard"
            size="small"
            disabled
            sx={{ flex: 1 }}
          />
        </Box>

        {type === 'Netbeheerder' && (
          <SelectDisciplines
            projectType={projectType}
            value={disciplines}
            onChange={(value) => {
              setDisciplines(value)
              setDisciplinesErrorText('')
            }}
            errorText={disciplinesErrorText}
          />
        )}

        <StyledError error={error} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} autoFocus>
          Sluiten
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          endIcon={<AddIcon />}
        >
          Toevoegen
        </Button>
      </DialogActions>
    </Dialog>
  )
}
